import { ComponentPropsWithoutRef, FC } from 'react';
import { IonButton, IonButtons, IonHeader, IonSpinner, IonTitle, IonToolbar } from '@ionic/react';
import ChevronLeftIcon from '../../assets/icons/ChevronLeftIcon';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Button from '../../components/buttons/Button';
import CloseCircleIcon from '../../../../assets/icons/CloseCircleIcon';

type ModalHeaderType = 'modal' | 'page';

interface ModalHeaderProps extends ComponentPropsWithoutRef<typeof IonHeader> {
  onBack?: () => void;
  title: string;
  endSlot?: JSX.Element;
  endText?: string;
  loading?: boolean;
  type?: ModalHeaderType;
  onClose?: () => void;
  onEndClick?: () => void;
}

const ModalHeader: FC<ModalHeaderProps> = ({
  onBack,
  onClose,
  onEndClick,
  endSlot,
  className,
  title,
  endText,
  loading,
  type = 'modal',
  ...rest
}) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Common:Components:Modal:ModalHeader',
  });
  const combinedClassName = classNames('', className);

  const EndSlot = (): JSX.Element => {
    if (loading) {
      return (
        <IonButtons slot="end">
          <IonSpinner name="crescent" />
        </IonButtons>
      );
    }
    if (endText) {
      return (
        <IonButtons slot="end">
          <Button title={endText} design="link" onClick={onEndClick} />
        </IonButtons>
      );
    }
    if (endSlot) {
      return endSlot;
    }
    return (
      <IonButtons slot="end" className="min-h-fit absolute right-0 overflow-visible">
        <div className="!min-h-fit [&::part(native)]:p-sm z-10 overflow-visible relative" onClick={onClose}>
          <CloseCircleIcon containerClassName="!shadow-section m-sm" className="size-[10px]" />
        </div>
      </IonButtons>
    );
  };

  return (
    <IonHeader
      className={combinedClassName}
      {...rest}
      style={{
        '--ion-safe-area-top': '0px',
        '--ion-safe-area-bottom': '0px',
      }}
    >
      <IonToolbar
        className={classNames('ion-color [&::part(container)]:p-0 !px-none overflow-visible', {
          '!py-xs': type === 'page',
        })}
      >
        <div className="flex items-center justify-between px-4">
          {!!onBack && (
            <IonButtons className="min-h-4" slot="start">
              <IonButton onClick={onBack} className="[&::part(native)]:p-0 !mx-0 !min-h-4 space-x-3">
                <ChevronLeftIcon className="stroke-brand-primary" />
                <span className="text-brand-primary font-semibold text-base">{t('buttons.back')}</span>
              </IonButton>
            </IonButtons>
          )}
          <IonTitle className="p-0 font-normal text-base">{title}</IonTitle>
          <EndSlot />
        </div>
      </IonToolbar>
    </IonHeader>
  );
};

export default ModalHeader;
